import { useIsTouchDevice } from 'designSystem/hooks/useIsTouchDevice'
import { AliasHighlightOverlay } from 'features/knowledgeDocumentViewer/components/viewer/search/AliasHighlightOverlay'
import { DocReferenceOverlay } from 'features/knowledgeDocumentViewer/components/viewer/search/DocReferenceOverlay'
import {
  type SearchHighlightData,
  SearchHighlightOverlay,
} from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { CrossReferenceMarker } from 'features/knowledgeDocumentViewer/components/viewer/shared/CrossReferenceMarker'
import { useCrossReferenceContext } from 'features/knowledgeDocumentViewer/contexts/CrossReferenceContext'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import {
  ActionMode,
  useKnowledgeDocumentUXContextOptional,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  FlowAnimationLayer,
  type FlowAnimationLayerProps,
} from 'features/knowledgeDocumentViewer/graph/FlowAnimationLayer'
import { setFlowScale } from 'features/knowledgeDocumentViewer/graph/flowScale'
import {
  canvasRectFromBox,
  nodeOverlayBounds,
  rectsOverlap,
} from 'features/knowledgeDocumentViewer/graph/graphInstrumentation'
import {
  GraphNodeOverlayLayer,
  type GraphNodeOverlayLayerProps,
} from 'features/knowledgeDocumentViewer/graph/GraphNodeOverlayLayer'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { shouldBoxBeDisabled } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import { crossReferenceRegion } from 'features/knowledgeDocumentViewer/utils/crossReferenceContext'
import {
  effectiveLinkHighlights,
  VIEWER_PREFERENCES_DEFAULTS,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { LabelerOverlay } from 'features/labeler/components/LabelerOverlay'
import { useLabelerOptional } from 'features/labeler/LabelerContext'
import React, { useCallback, useLayoutEffect, useMemo } from 'react'

import type { LiveValueChip } from './hooks/useLiveValueChips'
import { LiveValueChipLayer } from './LiveValueChipLayer'
import { OverlaySvg } from './styles'
import { TimeSeriesIndicatorIcon } from './TimeSeriesIndicatorIcon'
import { Box } from './types'

const BOX_EXPAND_BY = 2
const BOX_SHIFT_X = 1
const BOX_SHIFT_Y = 0

type SelectionBox = {
  x: number
  y: number
  width: number
  height: number
}

type ViewerOverlaySvgProps = {
  flowLayer?: FlowAnimationLayerProps & { pxPerImagePx?: number }
  nodeOverlayLayer?: GraphNodeOverlayLayerProps
  showCrossReferenceMarker?: boolean
  actionMode: ActionMode
  boxes: Box[]
  disableBackgroundClick?: boolean
  fillFor: (box: Box) => string
  firstPageWidth?: number
  groupPointerEvents?: React.CSSProperties['pointerEvents']
  hasTimeSeriesData: (label: string | undefined) => boolean
  imageNatural: { w: number; h: number }
  isDragging: boolean
  isMultipage: boolean
  liveChipGlowPxPerImagePx?: number
  liveChips?: LiveValueChip[]
  onBackgroundClick?: () => void
  onBoxAuxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxContextMenu?: (box: Box, event: React.MouseEvent) => void
  onBoxMouseEnter: (box: Box, event: React.MouseEvent<SVGElement>) => void
  onBoxMouseLeave: () => void
  onAliasClick?: (highlight: AliasHighlight) => void
  onAliasHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
  onDocReferenceClick?: (highlight: AliasHighlight) => void
  onDocReferenceHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
  onMouseDown: React.MouseEventHandler<SVGSVGElement>
  onMouseMove: React.MouseEventHandler<SVGSVGElement>
  onMouseUp: React.MouseEventHandler<SVGSVGElement>
  overlayStyle?: React.CSSProperties
  rectPointerEvents?: (
    isDisabled: boolean
  ) => React.CSSProperties['pointerEvents']
  searchHighlights?: SearchHighlightData
  selectionBox: SelectionBox | null
  strokeFor: (box: Box) => string
  svgRef: React.MutableRefObject<SVGSVGElement | null>
}

function ViewerOverlaySvgComponent({
  flowLayer,
  showCrossReferenceMarker = true,
  actionMode,
  nodeOverlayLayer,
  boxes,
  disableBackgroundClick = false,
  fillFor,
  firstPageWidth,
  groupPointerEvents,
  hasTimeSeriesData,
  imageNatural,
  isDragging,
  isMultipage,
  liveChipGlowPxPerImagePx,
  liveChips = [],
  onBackgroundClick,
  onBoxAuxClick,
  onBoxClick,
  onBoxContextMenu,
  onBoxMouseEnter,
  onBoxMouseLeave,
  onAliasClick,
  onAliasHover,
  onDocReferenceClick,
  onDocReferenceHover,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  overlayStyle,
  rectPointerEvents = () => 'auto',
  searchHighlights,
  selectionBox,
  strokeFor,
  svgRef,
}: ViewerOverlaySvgProps) {
  // Optional: this overlay also renders outside the UX provider.
  const uxContext = useKnowledgeDocumentUXContextOptional()
  const photoMode = uxContext?.photoMode ?? false
  const linkHighlights = effectiveLinkHighlights(
    uxContext?.linkHighlights ?? VIEWER_PREFERENCES_DEFAULTS.linkHighlights,
    photoMode
  )
  // The sidebar's subject stays highlighted even with asset highlights off,
  // so the alias overlay must mount whenever a panel subject is showing.
  const documentData = useDocumentTabDataContextOptional()
  const panelSubjectUid = documentData?.panelSubjectUid
  const crossReference = useCrossReferenceContext()
  const marker = crossReference?.marker
  const markerRight = Boolean(
    isMultipage && documentData && marker?.page === documentData.pageNumber + 1
  )
  const markerVisible =
    showCrossReferenceMarker &&
    marker &&
    documentData &&
    (marker.page === documentData.pageNumber || markerRight)
  const markerPageWidth =
    isMultipage && firstPageWidth
      ? markerRight
        ? imageNatural.w - firstPageWidth
        : firstPageWidth
      : imageNatural.w
  const markerSize = (markerPageWidth * 0.035) / 3
  const crossReferenceMenu = crossReference?.onContextMenu
  // Labeling mode owns the canvas: the subject/OPC highlight overlays and
  // the OCR boxes stand down so they cannot intercept or clutter the work.
  const labelerActive = useLabelerOptional()?.active ?? false
  const isEvalSessionLocked = uxContext?.isEvalSessionLocked ?? false
  const hidePageOverlays = labelerActive || isEvalSessionLocked
  const hideOcrBoxes = hidePageOverlays || photoMode

  const graphLinkedRects = useMemo(
    () =>
      (nodeOverlayLayer?.liveValueAppearances ?? []).flatMap((appearance) =>
        appearance.valueRect
          ? [nodeOverlayBounds(appearance.overlay), appearance.valueRect]
          : [nodeOverlayBounds(appearance.overlay)]
      ),
    [nodeOverlayLayer?.liveValueAppearances]
  )
  const visibleLiveChips = useMemo(
    () =>
      liveChips.filter(
        (chip) =>
          !graphLinkedRects.some((rect) =>
            rectsOverlap(rect, canvasRectFromBox(chip))
          )
      ),
    [liveChips, graphLinkedRects]
  )

  const chipLabels = useMemo(
    () => new Set(visibleLiveChips.map((chip) => chip.payload.label)),
    [visibleLiveChips]
  )

  const handleGraphAwareBoxEnter = useCallback(
    (box: Box, event: React.MouseEvent<SVGElement>): void => {
      if (!nodeOverlayLayer?.activeNodeIds.length) onBoxMouseEnter(box, event)
    },
    [nodeOverlayLayer?.activeNodeIds.length, onBoxMouseEnter]
  )

  const flowScale = flowLayer?.pxPerImagePx
  useLayoutEffect(() => {
    if (flowScale !== undefined) setFlowScale(svgRef.current, flowScale)
  }, [flowScale, svgRef])
  const flow =
    !hidePageOverlays && flowLayer ? (
      <FlowAnimationLayer
        paths={flowLayer.paths}
        isolating={flowLayer.isolating}
        markers={flowLayer.markers}
        highlights={flowLayer.highlights}
      />
    ) : null

  const handleSvgRef = useCallback(
    (node: SVGSVGElement | null) => {
      svgRef.current = node
    },
    [svgRef]
  )

  const isTouchDevice = useIsTouchDevice()
  const handleBoxClick = useCallback(
    (box: Box, event: React.MouseEvent<SVGElement>) => {
      if (isTouchDevice) onBoxMouseEnter(box, event)
      onBoxClick?.(box, event)
    },
    [isTouchDevice, onBoxClick, onBoxMouseEnter]
  )
  const handleBackgroundClick = useCallback(() => {
    if (isTouchDevice) onBoxMouseLeave()
    onBackgroundClick?.()
  }, [isTouchDevice, onBackgroundClick, onBoxMouseLeave])

  return (
    <OverlaySvg
      ref={handleSvgRef}
      role={nodeOverlayLayer?.overlays.length ? 'group' : 'presentation'}
      onContextMenu={(event) => {
        if (!crossReferenceMenu || hidePageOverlays || !documentData) return
        const matrix = event.currentTarget.getScreenCTM()
        if (!matrix) return
        const point = new DOMPoint(
          event.clientX,
          event.clientY
        ).matrixTransform(matrix.inverse())
        const right = Boolean(
          isMultipage && firstPageWidth && point.x >= firstPageWidth
        )
        const width =
          isMultipage && firstPageWidth
            ? right
              ? imageNatural.w - firstPageWidth
              : firstPageWidth
            : imageNatural.w
        if (width <= 0 || imageNatural.h <= 0) return
        const x = Math.max(
          0,
          Math.min(1, (point.x - (right ? (firstPageWidth ?? 0) : 0)) / width)
        )
        const y = Math.max(0, Math.min(1, point.y / imageNatural.h))
        const page = documentData.pageNumber + (right ? 1 : 0)
        crossReferenceMenu(
          {
            selected_text: '',
            source_page_number: page,
            point: [x, y],
            source_bbox: crossReferenceRegion(x, y),
          },
          event
        )
      }}
      style={overlayStyle}
      viewBox={`0 0 ${imageNatural.w} ${imageNatural.h}`}
      preserveAspectRatio='none'
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}>
      <g
        style={
          groupPointerEvents ? { pointerEvents: groupPointerEvents } : undefined
        }>
        <rect
          x={0}
          y={0}
          width={imageNatural.w}
          height={imageNatural.h}
          fill='transparent'
          style={{ pointerEvents: onBackgroundClick ? 'auto' : 'none' }}
          onClick={(event) => {
            if (
              !disableBackgroundClick &&
              event.target === event.currentTarget
            ) {
              handleBackgroundClick()
            }
          }}
        />

        {isMultipage && typeof firstPageWidth === 'number' && (
          <line
            x1={firstPageWidth}
            y1={0}
            x2={firstPageWidth}
            y2={imageNatural.h}
            stroke='black'
            strokeWidth={2}
            style={{ pointerEvents: 'none' }}
          />
        )}

        {!hidePageOverlays &&
          (linkHighlights.asset ||
            linkHighlights.assetClickable ||
            panelSubjectUid !== undefined) && (
            <AliasHighlightOverlay
              graphLinkedRects={graphLinkedRects}
              labelsWithReadings={chipLabels}
              imageNatural={imageNatural}
              firstPageWidth={firstPageWidth}
              isMultipage={isMultipage}
              showHighlights={linkHighlights.asset}
              interactive={linkHighlights.assetClickable}
              photoMode={photoMode}
              onAliasClick={onAliasClick}
              onAliasHover={
                nodeOverlayLayer?.activeNodeIds.length
                  ? undefined
                  : onAliasHover
              }
            />
          )}

        {!hidePageOverlays && linkHighlights.docReference && (
          <DocReferenceOverlay
            imageNatural={imageNatural}
            firstPageWidth={firstPageWidth}
            isMultipage={isMultipage}
            onDocReferenceClick={onDocReferenceClick}
            onDocReferenceHover={
              nodeOverlayLayer?.activeNodeIds.length
                ? undefined
                : onDocReferenceHover
            }
          />
        )}

        {!hideOcrBoxes &&
          boxes.map((box) => {
            const isDisabled = shouldBoxBeDisabled(box, actionMode)
            const expandBy = BOX_EXPAND_BY
            const shiftX = BOX_SHIFT_X
            const shiftY = BOX_SHIFT_Y
            const showTimeSeriesIndicator =
              hasTimeSeriesData(box.label) &&
              !graphLinkedRects.some((rect) =>
                rectsOverlap(
                  rect,
                  canvasRectFromBox({
                    x: box.x,
                    y: box.y,
                    width: box.w,
                    height: box.h,
                  })
                )
              ) &&
              !(box.label !== undefined && chipLabels.has(box.label))

            return (
              <g key={box.id}>
                <rect
                  data-box-label={box.label}
                  data-box-type={box.type}
                  x={box.x - expandBy + shiftX}
                  y={box.y - expandBy + shiftY}
                  width={box.w + expandBy * 2}
                  height={box.h + expandBy * 2}
                  rx={2}
                  ry={2}
                  fill={fillFor(box)}
                  stroke={strokeFor(box)}
                  strokeWidth={1.5}
                  style={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    pointerEvents: rectPointerEvents(isDisabled),
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                  onMouseEnter={(event) => {
                    if (!isDisabled) {
                      handleGraphAwareBoxEnter(box, event)
                    }
                  }}
                  onMouseLeave={onBoxMouseLeave}
                  onClick={(event) => {
                    if (!isDisabled) {
                      handleBoxClick(box, event)
                    }
                  }}
                  onAuxClick={(event) => {
                    if (!isDisabled) {
                      onBoxAuxClick?.(box, event)
                    }
                  }}
                  onContextMenu={(event) => {
                    if (!isDisabled) {
                      onBoxContextMenu?.(box, event)
                    }
                  }}
                />

                {showTimeSeriesIndicator && (
                  <TimeSeriesIndicatorIcon
                    x={box.x + box.w - 6 + shiftX}
                    y={box.y - 10 + shiftY}
                    size={14}
                  />
                )}
              </g>
            )
          })}

        {!hidePageOverlays && (
          <LiveValueChipLayer
            chips={visibleLiveChips}
            actionMode={actionMode}
            glowPxPerImagePx={liveChipGlowPxPerImagePx}
            onBoxMouseEnter={handleGraphAwareBoxEnter}
            onBoxMouseLeave={onBoxMouseLeave}
            onBoxClick={handleBoxClick}
            onBoxAuxClick={onBoxAuxClick}
            onBoxContextMenu={onBoxContextMenu}
            onAliasClick={onAliasClick}
            onAliasHover={
              nodeOverlayLayer?.activeNodeIds.length ? undefined : onAliasHover
            }
          />
        )}

        {isEvalSessionLocked ? null : (
          <LabelerOverlay imageNatural={imageNatural} />
        )}

        {searchHighlights && (
          <SearchHighlightOverlay
            highlights={searchHighlights}
            imageNatural={imageNatural}
            firstPageWidth={firstPageWidth}
            isMultipage={isMultipage}
          />
        )}

        {!hidePageOverlays &&
          (nodeOverlayLayer && nodeOverlayLayer.overlays.length > 0 ? (
            <GraphNodeOverlayLayer
              {...nodeOverlayLayer}
              glowPxPerImagePx={liveChipGlowPxPerImagePx}>
              {flow}
            </GraphNodeOverlayLayer>
          ) : (
            flow
          ))}

        {markerVisible && (
          <CrossReferenceMarker
            x={
              (markerRight ? (firstPageWidth ?? 0) : 0) +
              marker.point[0] * markerPageWidth -
              markerSize / 2
            }
            y={marker.point[1] * imageNatural.h - markerSize / 2}
            size={markerSize}
            loading={marker.loading}
          />
        )}
        {isDragging && selectionBox && (
          <rect
            x={selectionBox.x}
            y={selectionBox.y}
            width={selectionBox.width}
            height={selectionBox.height}
            fill='rgba(59, 130, 246, 0.1)'
            stroke='rgba(59, 130, 246, 0.8)'
            strokeWidth={2}
            strokeDasharray='5,5'
            style={{ pointerEvents: 'none' }}
          />
        )}
      </g>
    </OverlaySvg>
  )
}

export const ViewerOverlaySvg = React.memo(ViewerOverlaySvgComponent)
